import { Fragment, useMemo } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Download,
  Loader2,
} from "lucide-react";
import { useBiometrics, useDashboardChart } from "@/hooks/api";
import {
  buildFurstIdGroups,
  expandToFurstGroups,
  getDefaultBioIds,
} from "@/lib/utils/bloodTestSuggestion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { Hint } from "@/components/ui/hint";
import { downloadCsv, toCsv } from "@/lib/utils/csv";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
} from "./dashboard-table";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { FiltersCard } from "./filters-card";

const DASHBOARD_ID = "helfo-rules-v2";

// Semicolon and a BOM so Norwegian Excel opens the file in columns with æøå intact.
const CSV_DELIMITER = ";";

/** null = a count of 1–4, hidden by the backend. */
type Count = number | null;

type ExclusionRow = { name: string; visits: Count };
type MarkerRow = {
  id: string;
  name: string;
  /** Fürst contract price in kroner; null when the catalog has none. */
  price: string | null;
  v1: Count;
  v2: Count;
  removed: Count;
  /** Of the removals, the visits the private rules ask for it on. */
  removedToPrivat: Count;
  added: Count;
  knownCondition: Count;
  controlInterval: Count;
  rules: Count;
  /** Which known conditions removed this marker, and on how many visits. */
  conditions: ExclusionRow[];
  /** Which v1 clusters lost this marker in their v2 panel. */
  clusters: ExclusionRow[];
};
type ClusterRow = {
  name: string;
  v1: Count;
  v2: Count;
  triggers: string[];
  exclusions: string[];
  changes: string[];
};
type ReasonRow = {
  kind: "known_condition" | "control_interval" | "rules";
  condition: string | null;
  visits: Count;
  markers: Count;
};
type Comparison = {
  hidden: boolean;
  minGroup: number;
  minCell: number;
  visits: Count;
  summary?: Record<
    | "withHelfoV1"
    | "withHelfoV2"
    | "markersV1"
    | "markersV2"
    | "changed"
    | "lostAllHelfo"
    | "markersRemoved"
    | "markersAdded",
    Count
  >;
  markers?: MarkerRow[];
  clusters?: ClusterRow[];
  reasons?: ReasonRow[];
};

const countColumn = <T,>(
  id: string,
  label: string,
  value: (r: T) => Count,
): DashboardTableColumn<T> => ({
  id,
  label,
  value,
  numeric: true,
  align: "right",
});

/** `always`: in the B2C default panel, which goes on the private requisition of every visit. */
type MarkerViewRow = MarkerRow & { always: boolean };

const COUNT_COLUMNS: DashboardTableColumn<MarkerViewRow>[] = [
  countColumn("v1", "Dagens regler", (r) => r.v1),
  countColumn("v2", "Nye regler", (r) => r.v2),
  countColumn("removed", "Fjernet", (r) => r.removed),
  countColumn("added", "Lagt til", (r) => r.added),
  countColumn("knownCondition", "Kjent sykdom", (r) => r.knownCondition),
  countColumn("controlInterval", "12 mnd-intervall", (r) => r.controlInterval),
  countColumn("rules", "Panel/klynge endret", (r) => r.rules),
];

const formatPrice = (price: string | null) =>
  price == null
    ? "—"
    : Number(price).toLocaleString("no-NO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const MARKER_COLUMNS: DashboardTableColumn<MarkerViewRow>[] = [
  { id: "name", label: "Markør", value: (r) => r.name },
  ...COUNT_COLUMNS,
  {
    id: "always",
    label: (
      <Hint text="I standardpanelet for B2C. Tas privat på hvert besøk, så en markør HELFO slipper blir likevel tatt.">
        Alltid tatt
      </Hint>
    ),
    labelText: "Alltid tatt",
    value: (r) => (r.always ? "Ja" : "Nei"),
    filterable: true,
  },
  {
    id: "privat",
    label: (
      <Hint text="Antall besøk som mistet den på HELFO der privat-reglene ber om den. Tom for markører i standardpanelet, som alltid tas. Anbefalinger fra spørreskjemaets svaralternativer er ikke med.">
        Flyttet til privat
      </Hint>
    ),
    labelText: "Flyttet til privat",
    value: (r) => (r.always ? "" : r.removedToPrivat),
    numeric: true,
    align: "right",
  },
  {
    id: "price",
    label: "Pris (kr)",
    value: (r) => (r.price == null ? "" : Number(r.price)),
    numeric: true,
    align: "right",
  },
];

const reasonLabel = (r: ReasonRow) =>
  r.kind === "known_condition"
    ? `Kjent sykdom: ${r.condition}`
    : r.kind === "control_interval"
      ? "Tidligere unormalt svar, under 12 mnd siden"
      : "Panel eller klynge endret";

const REASON_COLUMNS: DashboardTableColumn<ReasonRow>[] = [
  { id: "reason", label: "Årsak", value: reasonLabel },
  countColumn("visits", "Besøk", (r) => r.visits),
  countColumn("markers", "Markører fjernet", (r) => r.markers),
];

const bullets = (title: string, lines: string[]) =>
  lines.length > 0 ? `${title}\n${lines.map((l) => `• ${l}`).join("\n")}` : "";

const clusterHint = (r: ClusterRow) =>
  [
    bullets("Utløses av", r.triggers),
    bullets("Ikke når", r.exclusions),
    bullets("Nye regler", r.changes),
  ]
    .filter(Boolean)
    .join("\n\n");

const CLUSTER_COLUMNS: DashboardTableColumn<ClusterRow>[] = [
  { id: "name", label: "Klynge", value: (r) => r.name },
  countColumn("v1", "Dagens regler", (r) => r.v1),
  countColumn("v2", "Nye regler", (r) => r.v2),
];

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().split("T")[0]!;
}

export function HelfoRulesV2Dashboard() {
  const [{ from, to, expanded }, update] = useDashboardUrlState({
    from: isoDaysAgo(180),
    to: isoDaysAgo(0),
    expanded: "",
  });
  const params = useMemo(() => ({ from, to }), [from, to]);
  const comparison = useDashboardChart<typeof params, Comparison>(
    DASHBOARD_ID,
    "comparison",
    params,
  );
  const result = comparison.data?.[0];
  const minCell = result?.minCell ?? 5;
  const fmt = (n: Count | undefined) =>
    n == null ? `<${minCell}` : n.toLocaleString("no-NO");

  // The page waits for the catalog too: without it every marker would read as
  // not always taken.
  const catalog = useBiometrics();
  const allBiometrics = catalog.data;
  // Whole Fürst codes, as the requisition flow seeds them.
  const alwaysIds = useMemo(() => {
    const all = allBiometrics ?? [];
    return expandToFurstGroups(getDefaultBioIds(all), buildFurstIdGroups(all));
  }, [allBiometrics]);
  const markerRows = useMemo(
    () => result?.markers?.map((m) => ({ ...m, always: alwaysIds.has(m.id) })),
    [result?.markers, alwaysIds],
  );

  const markers = useDashboardTable("markers", MARKER_COLUMNS, markerRows);
  const reasons = useDashboardTable("reasons", REASON_COLUMNS, result?.reasons);
  const clusters = useDashboardTable(
    "clusters",
    CLUSTER_COLUMNS,
    result?.clusters,
  );

  const exportCsv = <T,>(
    name: string,
    columns: DashboardTableColumn<T>[],
    rows: readonly T[],
  ) => {
    const csv = toCsv(
      columns.map((c) => c.labelText ?? String(c.label)),
      rows.map((r) =>
        columns.map((c) =>
          c.numeric ? fmt(c.value!(r) as Count) : c.value!(r),
        ),
      ),
      CSV_DELIMITER,
    );
    downloadCsv(csv, `helfo-regelendring-${name}-${from}-${to}.csv`, {
      bom: true,
    });
  };

  // One line per expanded reason, with the marker's own columns repeated, so
  // the file holds everything behind the rows, not just the collapsed view.
  const exportMarkers = () => {
    const rows = markers.viewRows.flatMap((r) => {
      const parent = MARKER_COLUMNS.map((c) =>
        c.numeric ? fmt(c.value!(r) as Count) : String(c.value!(r) ?? ""),
      );
      const details = [
        ...r.conditions.map((d) => ["Kjent sykdom", d.name, fmt(d.visits)]),
        ...r.clusters.map((d) => ["Klynge", d.name, fmt(d.visits)]),
      ];
      if (details.length === 0) return [[...parent, "", "", ""]];
      return details.map((d) => [...parent, ...d]);
    });
    const csv = toCsv(
      [
        ...MARKER_COLUMNS.map((c) => c.labelText ?? String(c.label)),
        "Årsakstype",
        "Årsak",
        "Besøk",
      ],
      rows,
      CSV_DELIMITER,
    );
    downloadCsv(csv, `helfo-regelendring-markorer-${from}-${to}.csv`, {
      bom: true,
    });
  };

  const s = result?.summary;

  return (
    <div className="space-y-6">
      <FiltersCard>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">
              Rekvisisjon fra
            </span>
            <Input
              type="date"
              value={from}
              onChange={(e) => update({ from: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Til</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => update({ to: e.target.value })}
              className="w-44"
            />
          </label>
        </div>
        <p className="text-xs text-admin-text-subtle">
          Hvert besøk spilles av med anamnese og prøvesvar slik de var da
          rekvisisjonen ble laget, først med dagens regler og så med
          beslutningsstøtten fra september 2026. Manuelle endringer legen gjorde
          er ikke med. Legemiddelklyngene 1–5 regnes som kjent sykdom. Tall fra
          1 til {minCell - 1} vises som «&lt;{minCell}».
        </p>
      </FiltersCard>

      {comparison.isLoading || catalog.isLoading ? (
        <ChartSkeleton />
      ) : comparison.isError || catalog.isError ? (
        <ErrorBlock
          message={(comparison.error ?? catalog.error)?.message}
        />
      ) : !result || result.hidden || !s ? (
        <EmptyBlock>
          Færre enn {result?.minGroup ?? 20} kunder i perioden. Tallene skjules
          for å beskytte enkeltpersoner. Velg en lengre periode.
        </EmptyBlock>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <KpiCard label="Besøk" value={fmt(result.visits)} />
            <KpiCard
              label="Besøk med HELFO (dagens → nye)"
              value={`${fmt(s.withHelfoV1)} → ${fmt(s.withHelfoV2)}`}
            />
            <KpiCard
              label="HELFO-markører (dagens → nye)"
              value={`${fmt(s.markersV1)} → ${fmt(s.markersV2)}`}
            />
            <KpiCard
              label="Markører fjernet / lagt til"
              value={`${fmt(s.markersRemoved)} / ${fmt(s.markersAdded)}`}
            />
            <KpiCard label="Besøk som endres" value={fmt(s.changed)} />
            <KpiCard
              label="Besøk som mister all HELFO"
              value={fmt(s.lostAllHelfo)}
            />
          </div>

          <TableCard
            title="Per markør (antall besøk) — klikk for sykdom og klynge"
            onExport={exportMarkers}
            empty={!result.markers?.length}
          >
            <DashboardTable
              table={markers}
              columns={MARKER_COLUMNS}
              renderRow={(r) => {
                const open = expanded === r.id;
                const Chevron = open ? ChevronDown : ChevronRight;
                const detailsId = `helfo-marker-details-${r.id}`;
                const toggle = () => update({ expanded: open ? "" : r.id });
                return (
                  <Fragment key={r.id}>
                    <TableRow className="cursor-pointer" onClick={toggle}>
                      <TableCell>
                        <button
                          type="button"
                          aria-expanded={open}
                          aria-controls={detailsId}
                          aria-label={`${open ? "Skjul" : "Vis"} årsaker for ${r.name}`}
                          className="inline-flex items-center gap-1.5 rounded-sm text-left text-admin-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-admin-primary-focus"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggle();
                          }}
                        >
                          <Chevron
                            aria-hidden="true"
                            className="h-3.5 w-3.5 text-admin-text-subtle"
                          />
                          {r.name}
                        </button>
                      </TableCell>
                      {COUNT_COLUMNS.map((c) => (
                        <TableCell
                          key={c.id}
                          className="text-right tabular-nums"
                        >
                          {fmt(c.value!(r) as Count)}
                        </TableCell>
                      ))}
                      <TableCell>
                        {r.always && (
                          <Check aria-label="Ja" className="h-4 w-4" />
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.always ? null : fmt(r.removedToPrivat)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPrice(r.price)}
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow
                        id={detailsId}
                        className="bg-admin-card-hover hover:bg-admin-card-hover"
                      >
                        <TableCell
                          colSpan={MARKER_COLUMNS.length}
                          className="py-3"
                        >
                          {r.conditions.length === 0 &&
                          r.clusters.length === 0 ? (
                            <p className="pl-5 text-xs text-admin-text-subtle">
                              Ingen kjent sykdom eller klynge forklarer
                              fjerningen av denne markøren.
                            </p>
                          ) : (
                            <div className="grid gap-4 pl-5 sm:grid-cols-2">
                              <ExclusionList
                                title="Kjent sykdom (antall besøk)"
                                rows={r.conditions}
                                fmt={fmt}
                              />
                              <ExclusionList
                                title="Panel/klynge (antall besøk)"
                                rows={r.clusters}
                                fmt={fmt}
                              />
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              }}
            />
          </TableCard>

          <TableCard
            title="Hvorfor markører fjernes"
            onExport={() =>
              exportCsv("arsaker", REASON_COLUMNS, reasons.viewRows)
            }
            empty={!result.reasons?.length}
          >
            <DashboardTable
              table={reasons}
              columns={REASON_COLUMNS}
              renderRow={(r) => (
                <TableRow key={reasonLabel(r)}>
                  <TableCell>{reasonLabel(r)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(r.visits)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(r.markers)}
                  </TableCell>
                </TableRow>
              )}
            />
          </TableCard>

          <TableCard
            title="Klynger som slår til (antall besøk)"
            onExport={() =>
              exportCsv("klynger", CLUSTER_COLUMNS, clusters.viewRows)
            }
            empty={!result.clusters?.length}
          >
            <DashboardTable
              table={clusters}
              columns={CLUSTER_COLUMNS}
              renderRow={(r) => (
                <TableRow key={r.name}>
                  <TableCell>
                    <Hint text={clusterHint(r)}>{r.name}</Hint>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(r.v1)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(r.v2)}
                  </TableCell>
                </TableRow>
              )}
            />
          </TableCard>
        </>
      )}
    </div>
  );
}

function TableCard({
  title,
  onExport,
  empty,
  children,
}: {
  title: string;
  onExport: () => void;
  empty: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle>{title}</CardTitle>
        <Button variant="outline" size="sm" onClick={onExport} disabled={empty}>
          <Download className="h-3 w-3" />
          Eksporter CSV
        </Button>
      </CardHeader>
      <CardContent>
        {empty ? (
          <EmptyBlock>Ingen endringer i perioden.</EmptyBlock>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

function ExclusionList({
  title,
  rows,
  fmt,
}: {
  title: string;
  rows: ExclusionRow[];
  fmt: (n: Count | undefined) => string;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-admin-text-subtle">{title}</p>
      {rows.length === 0 ? (
        <p className="mt-1 text-xs text-admin-text-subtle">Ingen</p>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {rows.map((r) => (
            <li
              key={r.name}
              className="flex items-baseline justify-between gap-4 text-sm"
            >
              <span className="text-admin-text-secondary">{r.name}</span>
              <span className="tabular-nums text-admin-text-primary">
                {fmt(r.visits)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="px-4 py-3">
        <p className="text-xs text-admin-text-subtle">{label}</p>
        <p className="text-lg font-bold text-admin-text-primary">{value}</p>
      </CardContent>
    </Card>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex items-center gap-2 text-admin-text-subtle text-sm py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" />
      Spiller av besøkene…
    </div>
  );
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-admin-text-subtle py-6 text-center">
      {children}
    </p>
  );
}

function ErrorBlock({ message }: { message?: string }) {
  return (
    <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-4 text-sm text-admin-danger-400">
      {message || "Ukjent feil"}
    </div>
  );
}
